import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Alert, AlertTitle } from '@/components/ui/alert'
import WizardSteps from '@/components/common/wizard/WizardSteps'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import Library from '@/components/mediaBuying/adCreatives/screens/aiAssetGeneration/ImageLibrary'
import RefineMediaDialog from '@/components/mediaBuying/adCreatives/screens/aiAssetGeneration/RefineMediaDialog'
import useImageMirroring from '@/components/mediaBuying/adCreatives/screens/aiAssetGeneration/useImageMirroring'
import GenerationProgress from '@/components/mediaBuying/adCreatives/screens/aiAssetGeneration/GenerationProgress'
import { useImageGeneration } from '@/components/mediaBuying/adCreatives/screens/aiAssetGeneration/useImageGeneration'

const Footer = ({ handleNextNavigation, handleBackNavigation, disabled }) => {
    return (
        <div className="tw:w-full">
            <div className="tw:flex tw:justify-end tw:gap-4 tw:w-full">
                <Button variant="secondary" className="tw:w-[156px] tw:!text-sm" onClick={handleBackNavigation}>
                    Back
                </Button>
                <Button onClick={handleNextNavigation} disabled={disabled} className="tw:w-[156px] tw:!text-sm">
                    Next
                </Button>
            </div>
        </div>
    )
}

const AiAssetLibrary = () => {
    const {
        isGenerating,
        generatedImages,
        selectedImages,
        setSelectedImages,
        showDeleteConfirmationModal,
        handleDelete,
        handleConfirmDelete,
        handleSelect,
        setShowDeleteConfirmationModal,
        generationProgress,
        handleStopGenerate,
        replaceAsset,
        trackGenerating,
    } = useImageGeneration()
    const [refining, setRefining] = useState(null)

    const {
        campaignId, goToStep,
        registerCustomNavigation,
        clearCustomNavigation,
        campaignFormat,
        markStepAs,
        dataForStepper,
        removeStepContentData,
    } = useWizard()

    const { isMirroring, mirrorImageAndNavigateToNextScreen } = useImageMirroring({
        images: generatedImages,
        selectedImages,
    })

    useEffect(() => {
        markStepAs(0)

        // Clear any stale assetId when entering the library screen
        // This ensures that when creating a new ad or returning to the library,
        // we start with a clean slate and don't use cached asset IDs from previous sessions
        removeStepContentData('setMediaCrop', 'assetId')
        removeStepContentData('carouselCards', 'assetId')
    }, [])

    const handleBackNavigation = () => {
        markStepAs(0)
        goToStep('uploadAssets')
    }

    useEffect(() => {
        const customNav = (
            <Footer
                handleBackNavigation={handleBackNavigation}
                handleNextNavigation={mirrorImageAndNavigateToNextScreen}
                disabled={isMirroring || selectedImages.length === 0}
            />
        )
        registerCustomNavigation(customNav)

        return () => clearCustomNavigation()
    }, [selectedImages, isMirroring])

    return (
        <div className="tw:flex tw:h-full">
            <WizardSteps adCreative={dataForStepper} currentStep="aiAssetLibrary" />
            <div className="flex-grow-1 tw:px-6 tw:py-4 tw:overflow-visible">
                <div>
                    <span className="tw:text-base tw:font-semibold">Select from My AI Generations Library</span>

                    {isMirroring && (
                        <Alert className="tw:text-left tw:mt-6">
                            <Loader2 className="tw:h-4 tw:w-4 tw:animate-spin" />
                            <AlertTitle className="tw:text-left">Please wait while we process your images.</AlertTitle>
                        </Alert>
                    )}

                    {isGenerating && generationProgress.total > 0 && (
                        <div>
                            <GenerationProgress progress={generationProgress} />

                            <Button variant="destructiveSoft" onClick={handleStopGenerate} className="tw:!mt-6">
                                Stop Generation
                            </Button>
                        </div>
                    )}

                    <Library
                        showHeader={true}
                        onDelete={handleDelete}
                        onRefine={setRefining}
                        images={generatedImages}
                        selectedImages={selectedImages}
                        handleConfirmDelete={handleConfirmDelete}
                        onClearSelection={() => setSelectedImages([])}
                        showDeleteConfirmationModal={showDeleteConfirmationModal}
                        onSelect={(id) => handleSelect(campaignFormat === 'carousel', id)}
                        handleDeleteConfirmationCancel={() => setShowDeleteConfirmationModal(false)}
                    />
                    {refining && (
                        <RefineMediaDialog
                            mediaType="IMAGE"
                            asset={refining}
                            campaignId={campaignId}
                            open
                            onClose={(pending) => {
                                setRefining(null)
                                if (pending) trackGenerating(pending, refining)
                            }}
                            onReplaced={(asset) => {
                                replaceAsset(refining, asset)
                                setRefining(null)
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    )
}

export default AiAssetLibrary
